// Clock Math Utilities

export interface HandAngles {
  hour: number;
  minute: number;
  second: number;
}

export interface Point {
  x: number;
  y: number;
}

// Get rotation degrees for CSS transform
export function getRotationDegrees(time: Date) {
  const { hour, minute, second } = getHandAngles(time);
  return {
    hoursDegrees: hour,
    minutesDegrees: minute,
    secondsDegrees: second
  };
}

// Get clock hand angles from time
export function getHandAngles(time: Date): HandAngles {
  const hours = time.getHours() % 12;
  const minutes = time.getMinutes();
  const seconds = time.getSeconds();
  const milliseconds = time.getMilliseconds();

  // Hour hand: moves continuously (each hour = 30°, each minute = 0.5°)
  const hourAngle = (hours * 30) + (minutes * 0.5) + (seconds * 0.5 / 60);

  // Minute hand: moves continuously (each minute = 6°, each second = 0.1°)
  const minuteAngle = (minutes * 6) + (seconds * 0.1) + (milliseconds * 0.1 / 1000);

  // Second hand: discrete tick — one step per second (60 ticks/minute), not
  // continuous sweep, so a "second" reads clearly. Intentionally ignores `milliseconds`.
  const secondAngle = seconds * 6;

  return {
    hour: hourAngle,
    minute: minuteAngle,
    second: secondAngle
  };
}

// Convert time (hour, minute) to angle on clock
export function timeToAngle(hours: number, minutes: number): number {
  // Normalize to 12-hour format
  const hour12 = hours % 12;
  
  // Calculate angle (12 o'clock = 0°, moving clockwise)
  const angle = (hour12 * 30) + (minutes * 0.5);
  
  return angle;
}

// Convert angle to radians
export function degToRad(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

// Convert polar coordinates to Cartesian
export function polarToCartesian(cx: number, cy: number, radius: number, angleDegrees: number): Point {
  // Clock starts at 12 o'clock (top), so subtract 90 to align
  const angleRadians = degToRad(angleDegrees - 90);
  
  return {
    x: cx + radius * Math.cos(angleRadians),
    y: cy + radius * Math.sin(angleRadians)
  };
}

// Create SVG arc path for task wedge
export function describeArc(
  cx: number,
  cy: number,
  radius: number,
  startAngle: number,
  endAngle: number
): string {
  // Handle crossing midnight (e.g., 23:55 to 00:30)
  let adjustedEndAngle = endAngle;
  if (endAngle < startAngle) {
    adjustedEndAngle = endAngle + 360;
  }

  const start = polarToCartesian(cx, cy, radius, adjustedEndAngle);
  const end = polarToCartesian(cx, cy, radius, startAngle);

  const largeArcFlag = adjustedEndAngle - startAngle <= 180 ? '0' : '1';

  const d = [
    'M', start.x, start.y,
    'A', radius, radius, 0, largeArcFlag, 0, end.x, end.y
  ].join(' ');

  return d;
}

// Get mid-angle of a wedge
export function getMidAngle(startAngle: number, endAngle: number): number {
  if (endAngle < startAngle) {
    endAngle += 360;
  }
  return (startAngle + endAngle) / 2;
}

// Calculate bounding box for a label at given position
export function getLabelBoundingBox(
  x: number,
  y: number,
  width: number,
  height: number
): { x: number; y: number; width: number; height: number } {
  return {
    x: x - width / 2,
    y: y - height / 2,
    width,
    height
  };
}

// Check if two bounding boxes intersect
export function boxesIntersect(
  box1: { x: number; y: number; width: number; height: number },
  box2: { x: number; y: number; width: number; height: number }
): boolean {
  return !(
    box1.x + box1.width < box2.x ||
    box2.x + box2.width < box1.x ||
    box1.y + box1.height < box2.y ||
    box2.y + box2.height < box1.y
  );
}

// Get time string from Date object
export function getTimeString(date: Date): string {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

// ============================================================================
// 24-HOUR CYCLE UTILITIES - Proportional Time-to-Angle Conversion
// ============================================================================

export interface TimeSegment {
  start: number; // minutes in [0, 1439]
  end: number;   // minutes in [0, 1439]
}

// 24-hour period in minutes
export const PERIOD_MINUTES = 24 * 60; // 1440 minutes

/**
 * Parse HH:MM string to minutes since midnight (0-1439)
 * @param timeStr - Time in "HH:MM" format (24-hour)
 * @returns Minutes since midnight (0-1439)
 */
export function parseTimeToMinutes(timeStr: string): number {
  if (!timeStr) return 0;
  const [hours, minutes] = timeStr.split(':').map(Number);
  return ((hours * 60) + minutes) % PERIOD_MINUTES;
}

/**
 * Convert minutes since midnight to angle on 24-hour clock
 * @param minutes - Minutes since midnight (0-1439)
 * @returns Angle in degrees (0° at 12 o'clock, clockwise)
 */
export function minutesToClockDeg(minutes: number): number {
  return 360 * (minutes / PERIOD_MINUTES);
}

/**
 * Convert time to angle on 24-hour clock (proportional representation)
 * @param hours - Hour (0-23)
 * @param minutes - Minutes (0-59)
 * @returns Angle in degrees (0° at 12 o'clock, clockwise)
 */
export function timeToAngle24h(hours: number, minutes: number): number {
  const totalMinutes = (hours * 60) + minutes;
  return minutesToClockDeg(totalMinutes);
}

/**
 * Split a time interval that may cross midnight into 1-2 segments
 * On a 24-hour dial, if end < start, the interval crosses midnight
 * @param startMin - Start time in minutes since midnight
 * @param endMin - End time in minutes since midnight
 * @returns Array of 1 or 2 segments (split at midnight if needed)
 */
export function splitInterval(startMin: number, endMin: number): TimeSegment[] {
  // If end >= start, it's a simple interval within the same day
  if (endMin >= startMin) {
    return [{ start: startMin, end: endMin }];
  }
  
  // Crosses midnight: split into two segments
  return [
    { start: startMin, end: PERIOD_MINUTES }, // From start to midnight (1440)
    { start: 0, end: endMin }                  // From midnight to end
  ];
}

/**
 * Create SVG path for a wedge (pizza slice) using 24-hour proportional angles
 * @param cx - Center X
 * @param cy - Center Y
 * @param radius - Wedge radius
 * @param startMin - Start time in minutes since midnight
 * @param endMin - End time in minutes since midnight
 * @returns SVG path string for the wedge
 */
export function createWedgePath24h(
  cx: number,
  cy: number,
  radius: number,
  startMin: number,
  endMin: number
): string {
  const startDeg = minutesToClockDeg(startMin);
  const endDeg = minutesToClockDeg(endMin);
  
  // Calculate angle difference (always positive, clockwise)
  const delta = ((endDeg - startDeg + 360) % 360);
  const largeArcFlag = delta > 180 ? 1 : 0;
  
  // Get start and end points on the circle
  const startPoint = polarToCartesian(cx, cy, radius, startDeg);
  const endPoint = polarToCartesian(cx, cy, radius, endDeg);
  
  // Create wedge path: center → start point → arc clockwise to end point → back to center
  return [
    'M', cx, cy,                    // Move to center
    'L', startPoint.x, startPoint.y, // Line to start
    'A', radius, radius, 0, largeArcFlag, 1, endPoint.x, endPoint.y, // Arc clockwise to end
    'Z'                              // Close path back to center
  ].join(' ');
}

/**
 * Create SVG path for a wedge using 12-hour clock angles
 * @param cx - Center X
 * @param cy - Center Y
 * @param radius - Wedge radius
 * @param startHours - Start hour (0-23)
 * @param startMinutes - Start minutes (0-59)
 * @param endHours - End hour (0-23)
 * @param endMinutes - End minutes (0-59)
 * @returns SVG path string for the wedge
 */
export function createWedgePath12h(
  cx: number,
  cy: number,
  radius: number,
  startHours: number,
  startMinutes: number,
  endHours: number,
  endMinutes: number
): string {
  // Convert to 12-hour angles (each hour = 30°, each minute = 0.5°)
  const startAngle = timeToAngle(startHours, startMinutes);
  let endAngle = timeToAngle(endHours, endMinutes);
  
  // Handle cases where end is before start (crossing 12-hour boundary)
  if (endAngle <= startAngle) {
    endAngle += 360;
  }
  
  const angleDelta = endAngle - startAngle;
  const largeArcFlag = angleDelta > 180 ? 1 : 0;
  
  // Get start and end points on the circle
  const startPoint = polarToCartesian(cx, cy, radius, endAngle % 360);
  const endPoint = polarToCartesian(cx, cy, radius, startAngle);
  
  // Create wedge path: center → start → arc → end → back to center
  return [
    'M', cx, cy,
    'L', startPoint.x, startPoint.y,
    'A', radius, radius, 0, largeArcFlag, 0, endPoint.x, endPoint.y,
    'Z'
  ].join(' ');
}

/**
 * Convert minutes (0-59) to angle on a 60-minute clock
 * @param minutes - Minutes (0-59)
 * @returns Angle in degrees (0° at 12 o'clock, clockwise)
 */
export function minutesToAngle(minutes: number): number {
  // Each minute = 6° (360° / 60 minutes)
  return minutes * 6;
}

/**
 * Create SVG path for a wedge on a 60-minute clock
 * Where the entire clock face represents 60 minutes (like a traditional minute hand)
 * @param cx - Center X
 * @param cy - Center Y
 * @param radius - Wedge radius
 * @param startMinutes - Start minute (0-59)
 * @param endMinutes - End minute (0-59)
 * @returns SVG path string for the wedge
 */
export function createWedgePath60min(
  cx: number,
  cy: number,
  radius: number,
  startMinutes: number,
  endMinutes: number
): string {
  // Convert minutes to angles (each minute = 6°)
  const startAngle = minutesToAngle(startMinutes);
  let endAngle = minutesToAngle(endMinutes);
  
  // Handle wrapping around the hour (e.g., from minute 50 to minute 10)
  if (endAngle <= startAngle) {
    endAngle += 360;
  }
  
  const angleDelta = endAngle - startAngle;
  const largeArcFlag = angleDelta > 180 ? 1 : 0;
  
  // Get start and end points on the circle
  const startPoint = polarToCartesian(cx, cy, radius, endAngle % 360);
  const endPoint = polarToCartesian(cx, cy, radius, startAngle);
  
  // Create wedge path: center → start → arc → end → back to center
  return [
    'M', cx, cy,
    'L', startPoint.x, startPoint.y,
    'A', radius, radius, 0, largeArcFlag, 0, endPoint.x, endPoint.y,
    'Z'
  ].join(' ');
}

/**
 * Check if two minute ranges overlap on a 60-minute clock
 * @param start1 - Start minute of first range (0-59)
 * @param end1 - End minute of first range (0-59)
 * @param start2 - Start minute of second range (0-59)
 * @param end2 - End minute of second range (0-59)
 * @returns true if ranges overlap
 */
export function minuteRangesOverlap(
  start1: number,
  end1: number,
  start2: number,
  end2: number
): boolean {
  // Normalize ranges to handle wrapping (e.g., 50-10 means 50-59, 0-10)
  const normalize = (start: number, end: number): [number, number][] => {
    if (end <= start) {
      // Wraps around the hour
      return [[start, 59], [0, end]];
    }
    return [[start, end]];
  };

  const ranges1 = normalize(start1, end1);
  const ranges2 = normalize(start2, end2);

  // Check if any segment overlaps
  for (const [s1, e1] of ranges1) {
    for (const [s2, e2] of ranges2) {
      // Simple overlap: [s1, e1] and [s2, e2]
      if (s1 <= e2 && s2 <= e1) {
        return true;
      }
    }
  }

  return false;
}

export interface TaskWithRadius {
  taskId: string;
  radius: number;
  layer: number;
}

/**
 * Calculate layered radii for overlapping tasks
 * Tasks that overlap will be rendered at different radii (layers)
 * @param tasks - Array of tasks with startTime and endTime
 * @param baseRadius - Base radius for innermost layer
 * @param layerSpacing - Spacing between layers (default: 25)
 * @param sortComparator - Optional comparator function to sort tasks (determines layering priority)
 * @param baseLayerExclusivePredicate - Optional predicate. If provided, tasks satisfying this get layer 0 (baseRadius).
 *                                      Tasks NOT satisfying this start at layer 1 (baseRadius + layerSpacing), 
 *                                      effectively reserving the "largest" radius exclusively for special tasks (like ACTIVE).
 * @returns Map of task IDs to their assigned radius
 */
export function calculateLayeredRadii<T extends { id: string; startTime: Date; endTime: Date; state: any; opacity: number }>(
  tasks: T[],
  baseRadius: number,
  layerSpacing: number = 25,
  sortComparator?: (a: T, b: T) => number,
  baseLayerExclusivePredicate?: (task: T) => boolean
): Map<string, TaskWithRadius> {
  const result = new Map<string, TaskWithRadius>();
  
  if (tasks.length === 0) return result;

  // Sort tasks by state priority. 
  // The FIRST task in the sorted list gets priority for the base layer (outermost/largest radius if layerSpacing is negative)
  const sortedTasks = [...tasks].sort(sortComparator || ((a, b) => {
    // Default behavior: Prioritize by opacity (lower opacity = inner layer, should disappear first)
    if (a.opacity !== b.opacity) {
      return a.opacity - b.opacity;
    }
    // Then by start time
    return a.startTime.getTime() - b.startTime.getTime();
  }));

  // Assign layers to tasks based on overlaps
  const taskLayers: number[] = new Array(sortedTasks.length).fill(0);

  for (let i = 0; i < sortedTasks.length; i++) {
    const task = sortedTasks[i];
    
    // If exclusive logic is enabled, verify if this task is allowed on layer 0
    // If predicate exists AND task fails it, it forces start at layer 1 (effectively shifting it inner)
    const forceInner = baseLayerExclusivePredicate && !baseLayerExclusivePredicate(task);
    const minLayer = forceInner ? 1 : 0;

    const startMin = task.startTime.getMinutes();
    const endMin = task.endTime.getMinutes();

    let maxOverlapLayer = minLayer - 1; // Start checking below min layer

    // Check against all previously processed tasks
    for (let j = 0; j < i; j++) {
      const otherTask = sortedTasks[j];
      const otherStartMin = otherTask.startTime.getMinutes();
      const otherEndMin = otherTask.endTime.getMinutes();

      if (minuteRangesOverlap(startMin, endMin, otherStartMin, otherEndMin)) {
        maxOverlapLayer = Math.max(maxOverlapLayer, taskLayers[j]);
      }
    }

    // Assign this task to the next available layer, but at least minLayer
    taskLayers[i] = Math.max(minLayer, maxOverlapLayer + 1);
  }

  // Create result map with calculated radii
  sortedTasks.forEach((task, index) => {
    const layer = taskLayers[index];
    result.set(task.id, {
      taskId: task.id,
      radius: baseRadius + (layer * layerSpacing),
      layer
    });
  });

  return result;
}


/**
 * Create SVG path for a rim style wedge (donut slice)
 * @param cx - Center X
 * @param cy - Center Y
 * @param outerRadius - Outer radius of the rim
 * @param innerRadius - Inner radius of the rim
 * @param startMinutes - Start minute (0-59)
 * @param endMinutes - End minute (0-59)
 * @returns SVG path string
 */
export function createRimWedgePath60min(
  cx: number,
  cy: number,
  outerRadius: number,
  innerRadius: number,
  startMinutes: number,
  endMinutes: number
): string {
  const startAngle = minutesToAngle(startMinutes);
  let endAngle = minutesToAngle(endMinutes);
  
  if (endAngle <= startAngle) {
    endAngle += 360;
  }
  
  const angleDelta = endAngle - startAngle;
  const largeArcFlag = angleDelta > 180 ? 1 : 0;
  
  // Calculate points
  // Note: Using existing convention where we draw counter-clockwise from end to start
  const outerStart = polarToCartesian(cx, cy, outerRadius, endAngle % 360);
  const outerEnd = polarToCartesian(cx, cy, outerRadius, startAngle);
  const innerStart = polarToCartesian(cx, cy, innerRadius, endAngle % 360);
  const innerEnd = polarToCartesian(cx, cy, innerRadius, startAngle);
  
  return [
    'M', innerStart.x, innerStart.y,              // Move to inner start (at end time)
    'L', outerStart.x, outerStart.y,              // Line to outer start (at end time)
    'A', outerRadius, outerRadius, 0, largeArcFlag, 0, outerEnd.x, outerEnd.y, // Arc to outer end (at start time)
    'L', innerEnd.x, innerEnd.y,                  // Line to inner end (at start time)
    'A', innerRadius, innerRadius, 0, largeArcFlag, 1, innerStart.x, innerStart.y, // Arc back to inner start
    'Z'
  ].join(' ');
}
